import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Flex,
  Icon,
  Input,
  FormControl,
  FormLabel,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdAssessment } from 'react-icons/md';
import AnalyticsApi from '../../../../api/analytics';
import { exportTransfersSummary } from '../../../../functions/generatePayrollFile';
import { getCompanies } from '../../../../functions/companiesCache';

const TransfersSummaryModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [companiesCache, setCompaniesCache] = useState({});
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const defaultStart = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (isOpen) {
      getCompanies()
        .then(setCompaniesCache)
        .catch(err => console.error('Error loading companies:', err));
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const startDatetime = `${startDate} 00:00:00`;
      const endDatetime = `${endDate} 00:00:00`;
      const response = await AnalyticsApi.getTransfersSummary(
        startDatetime,
        endDatetime,
        selectedCompanyId || null
      );
      setData(response.data);
    } catch (err) {
      console.error('Error fetching transfers summary:', err);
      setError('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setStartDate(defaultStart());
    setEndDate(new Date().toISOString().split('T')[0]);
    setSelectedCompanyId('');
    setData(null);
    setError(null);
  };

  const handleExport = () => {
    if (data && data.length > 0) {
      exportTransfersSummary(data, startDate, endDate);
    }
  };

  const totalApplications = data ? data.reduce((sum, r) => sum + Number(r.total_applications), 0) : 0;
  const totalUniqueUsers = data ? data.reduce((sum, r) => sum + Number(r.unique_users), 0) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="55vw" maxH="90vh">
        <ModalHeader borderBottom="1px" borderColor="gray.200" textAlign="center" fontSize="xl" fontWeight="bold" color="blue.600" py={5}>
          <Flex align="center" justify="center" gap={2}>
            <Icon as={MdAssessment} />
            <Text>סיכום העברות לפי חברה</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton left={4} />

        <ModalBody p={6}>
          <Box mb={6} p={4} border="1px" borderColor={borderColor} borderRadius="lg" bg={bgColor}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
              <FormControl>
                <FormLabel>תאריך התחלה</FormLabel>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>תאריך סיום (לא כולל)</FormLabel>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>חברה</FormLabel>
                <Select
                  value={selectedCompanyId}
                  onChange={e => setSelectedCompanyId(e.target.value)}
                  placeholder="כל החברות"
                >
                  {Object.entries(companiesCache).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
            <Flex justify="center" gap={3}>
              <Button colorScheme="blue" onClick={fetchData} isLoading={loading} px={8}>
                הצג נתונים
              </Button>
              <Button variant="outline" colorScheme="gray" onClick={handleClear} isDisabled={loading} px={6}>
                נקה
              </Button>
            </Flex>
          </Box>

          {loading ? (
            <VStack py={10}>
              <Spinner size="lg" />
              <Text>טוען נתונים...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>שגיאה!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : data && data.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">לא נמצאו נתונים לתקופה שנבחרה</Text>
            </Box>
          ) : data ? (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <HStack spacing={6}>
                  <Text fontSize="sm" color="gray.600">
                    סה"כ חברות: <b>{data.length}</b>
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    סה"כ בקשות: <b>{totalApplications.toLocaleString('he-IL')}</b>
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    סה"כ משתמשים ייחודיים: <b>{totalUniqueUsers.toLocaleString('he-IL')}</b>
                  </Text>
                </HStack>
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={handleExport}
                  isDisabled={!data || data.length === 0}
                >
                  ייצוא לאקסל
                </Button>
              </HStack>

              <Box borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
                <Table variant="simple" size="md" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '36%' }} />
                    <col style={{ width: '36%' }} />
                  </colgroup>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th textAlign="right" borderBottom="2px" borderColor="gray.200">חברה</Th>
                      <Th textAlign="center" borderBottom="2px" borderColor="gray.200">משתמשים ייחודיים</Th>
                      <Th textAlign="center" borderBottom="2px" borderColor="gray.200">סה"כ בקשות</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((row, idx) => (
                      <Tr key={row.company_id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'blue.50' }}>
                        <Td textAlign="right" fontWeight="medium">{row.company_name}</Td>
                        <Td textAlign="center" color="gray.700">{Number(row.unique_users).toLocaleString('he-IL')}</Td>
                        <Td textAlign="center" fontWeight="bold" color="blue.600">{Number(row.total_applications).toLocaleString('he-IL')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          ) : null}
        </ModalBody>

        <ModalFooter borderTop="1px" borderColor="gray.200" justifyContent="center">
          <Button onClick={onClose} colorScheme="gray" px={8}>
            סגור
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransfersSummaryModal;
